import React, { useEffect } from "react";
import { EditOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Form, Input } from "antd";
import ButtonWrapper from "../../components/reuseComp/ButtonWrapper";
import UploadFoodImage from "../../../lib/UploadFoodImage";

const { TextArea } = Input;

interface Point {
  heading?: string;
  description?: string;
}

interface WhyChooseUsData {
  heading?: string;
  description?: string;
  image?: string;
  points?: Point[];
}

interface WhyChooseUsProps {
  isEditing: boolean;
  setIsEditing: (value: boolean) => void;
  handleCancel: () => void;
  handleSave: () => void;
  form: any;
  whyChooseUsData?: WhyChooseUsData;
  loading?: boolean;
}

const WhyChooseUs: React.FC<WhyChooseUsProps> = ({
  isEditing,
  setIsEditing,
  handleCancel,
  handleSave,
  form,
  whyChooseUsData,
  loading,
}) => {

  useEffect(() => {
    if (whyChooseUsData) {
      form.setFieldsValue({
        whyChooseUs: {
          heading: whyChooseUsData.heading || "",
          description: whyChooseUsData.description || "",
          image: whyChooseUsData.image || "",
          points: whyChooseUsData.points || [],
        },
      });
    } else {
      form.resetFields();
    }
  }, [whyChooseUsData, form]);

  return (
    <div>
      {!isEditing ? (
        <>
          {/* Desktop Table */}

          <div className="rounded-[20px] overflow-hidden hidden sm:block">
            <table className="min-w-full border-collapse">
              <thead>
                <tr className="bg-primary text-white">
                  <th className="py-3 px-4 text-left">Heading</th>
                  <th className="py-3 px-4 text-left">Description</th>
                  <th className="py-3 px-4 text-center">Image</th>
                  <th className="py-3 px-4 text-left">Points</th>
                  <th className="py-3 px-4 text-end">Action</th>
                </tr>
              </thead>

              <tbody>
                {whyChooseUsData?.heading ? (
                  <tr className="bg-[#F9F9F9] border-b-2 border-[#e9e8e8]">
                    <td className="py-3 px-4">
                      {whyChooseUsData.heading || "—"}
                    </td>

                    <td className="py-3 px-4">
                      {whyChooseUsData.description || "—"}
                    </td>

                    <td className="py-3 px-4 text-center">
                      {whyChooseUsData.image ? (
                        <img
                          src={whyChooseUsData.image}
                          alt="why choose us"
                          className="h-[50px] object-contain mx-auto"
                        />
                      ) : (
                        "—"
                      )}
                    </td>

                    <td className="py-3 px-4">
                      {whyChooseUsData.points?.map((point, i) => (
                        <div key={i}>
                          <strong>{point.heading}</strong> - {point.description}
                        </div>
                      ))}
                    </td>

                    <td className="py-3 px-4 text-end text-primary">
                      <EditOutlined
                        onClick={() => setIsEditing(true)}
                        className="cursor-pointer text-[20px]"
                      />
                    </td>
                  </tr>
                ) : (
                  <tr className="bg-[#F9F9F9] border-b-2 border-[#e9e8e8]">
                    <td colSpan={5} className="text-center py-6">
                      <Button type="dashed" onClick={() => setIsEditing(true)}>
                        Add Data
                      </Button>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile View */}

          <div className="sm:hidden">
            {whyChooseUsData?.heading ? (
              <div className="bg-[#F9F9F9] border rounded-lg p-4 space-y-3">

                <div>
                  <span className="font-semibold text-primary block">
                    Heading
                  </span>
                  {whyChooseUsData.heading}
                </div>

                <div>
                  <span className="font-semibold text-primary block">
                    Description
                  </span>
                  {whyChooseUsData.description}
                </div>

                <div>
                  <span className="font-semibold text-primary block">
                    Image
                  </span>
                  {whyChooseUsData.image && (
                    <img
                      src={whyChooseUsData.image}
                      className="h-[60px] object-contain"
                    />
                  )}
                </div>

                <div>
                  <span className="font-semibold text-primary block">
                    Points
                  </span>

                  {whyChooseUsData.points?.map((point, i) => (
                    <p key={i}>
                      <strong>{point.heading}</strong> - {point.description}
                    </p>
                  ))}
                </div>

                <EditOutlined
                  onClick={() => setIsEditing(true)}
                  className="cursor-pointer text-[18px]"
                />
              </div>
            ) : (
              <div className="flex justify-center border rounded-lg p-4">
                <Button type="dashed" onClick={() => setIsEditing(true)}>
                  Add Data
                </Button>
              </div>
            )}
          </div>
        </>
      ) : (
        /* Edit Mode */

        <div>
          <div className="bg-[#F9F9F9] p-4 sm:p-6 rounded-lg sm:rounded-2xl shadow-sm">

            <Form layout="vertical" form={form}>

              <Form.Item
                label="Heading"
                name={["whyChooseUs", "heading"]}
                rules={[{ required: true, message: "Please enter heading" }]}
              >
                <Input placeholder="Enter heading" />
              </Form.Item>

              <Form.Item
                label="Description"
                name={["whyChooseUs", "description"]}
                rules={[{ required: true, message: "Please enter description" }]}
              >
                <TextArea rows={3} placeholder="Enter description" />
              </Form.Item>

              <Form.Item
                label="Image"
                name={["whyChooseUs", "image"]}
                rules={[{ required: true, message: "Please upload image" }]}
              >
                <UploadFoodImage maxUploadCount={1}/>
              </Form.Item>

              {/* Points */}

              <Form.List name={["whyChooseUs", "points"]}>
                {(fields, { add, remove }) => (
                  <>
                    {fields.map((field) => (
                      <div key={field.key} className="flex gap-3 mb-3 items-center">

                        <Form.Item
                          {...field}
                          name={[field.name, "heading"]}
                          rules={[{ required: true }]}
                          className="flex-1"
                        >
                          <Input placeholder="Point Heading" />
                        </Form.Item>

                        <Form.Item
                          {...field}
                          name={[field.name, "description"]}
                          rules={[{ required: true }]}
                          className="flex-1"
                        >
                          <Input placeholder="Point Description" />
                        </Form.Item>

                        <Button danger onClick={() => remove(field.name)}>
                          Remove
                        </Button>

                      </div>
                    ))}

                    <Button
                      type="dashed"
                      icon={<PlusOutlined />}
                      onClick={() => add()}
                    >
                      Add Point
                    </Button>

                  </>
                )}
              </Form.List>

            </Form>
          </div>

          <div className="flex gap-3 mt-6 sticky bottom-5">
            <ButtonWrapper
              type="primary"
              onClick={handleSave}
              loading={loading}
            >
              Save
            </ButtonWrapper>

            <ButtonWrapper onClick={handleCancel}>
              Cancel
            </ButtonWrapper>
          </div>
        </div>
      )}
    </div>
  );
};

export default WhyChooseUs;